// Production Configuration - Live Server
const API_BASE_URL = 'https://praisedevotion.co.za/api/';

import AsyncStorage from '@react-native-async-storage/async-storage';

class ApiService {
  // Authentication methods
  static async register(userData) {
    try {
      console.log('🔄 Starting register request...');
      console.log('📤 URL:', `${API_BASE_URL}api.php`);
      console.log('📤 UserData:', { ...userData, password: '[HIDDEN]' });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'register',
          ...userData
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Response data:', { ...data, token: data.token ? '[HIDDEN]' : undefined });
      
      if (data.success) {
        // Store token in AsyncStorage
        if (data.token) {
          await AsyncStorage.setItem('authToken', data.token);
          await AsyncStorage.setItem('user', JSON.stringify(data.user));
          console.log('✅ Token and user data stored successfully');
        }
        return data;
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏰ Registration request timed out');
        throw new Error('Request timed out. Please check your internet connection and try again.');
      }
      console.error('❌ API Error (register):', error);
      throw error;
    }
  }

  static async login(credentials) {
    try {
      console.log('🔄 Starting login request...');
      console.log('📤 URL:', `${API_BASE_URL}api.php`);
      console.log('📤 Credentials:', { login: credentials.login, password: '[HIDDEN]' });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          ...credentials
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Response data:', { ...data, token: data.token ? '[HIDDEN]' : undefined });
      
      if (data.success) {
        // Store token in AsyncStorage
        if (data.token) {
          await AsyncStorage.setItem('authToken', data.token);
          await AsyncStorage.setItem('user', JSON.stringify(data.user));
          console.log('✅ Token and user data stored successfully');
        }
        return data;
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏰ Login request timed out');
        throw new Error('Request timed out. Please check your internet connection and try again.');
      }
      console.error('❌ API Error (login):', error);
      console.error('❌ Error details:', error.message, error.stack);
      throw error;
    }
  }

  static async logout() {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (token) {
        // Use direct api.php call since .htaccess routing is broken
        await fetch(`${API_BASE_URL}api.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            action: 'logout'
          })
        });
      }
      
      // Clear AsyncStorage
      await AsyncStorage.removeItem('authToken');
      await AsyncStorage.removeItem('user');
      
      return { success: true };
    } catch (error) {
      console.error('API Error (logout):', error);
      // Still clear AsyncStorage even if API call fails
      await AsyncStorage.removeItem('authToken');
      await AsyncStorage.removeItem('user');
      return { success: true };
    }
  }

  // User management methods
  static async getUserProfile() {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}users/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.user;
      } else {
        throw new Error(data.error || 'Failed to fetch user profile');
      }
    } catch (error) {
      console.error('API Error (getUserProfile):', error);
      throw error;
    }
  }

  static async updateUserProfile(profileData) {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('API Error (updateUserProfile):', error);
      throw error;
    }
  }

  static async getUserBio(userId) {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}users/bio`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          data: data.data || {
            name: '',
            email: '',
            phone: '',
            website: '',
            location: '',
            bio: '',
            profile_image: '',
            links: []
          }
        };
      } else {
        throw new Error(data.error || 'Failed to fetch user bio');
      }
    } catch (error) {
      console.error('API Error (getUserBio):', error);
      return { success: false, error: error.message };
    }
  }

  static async updateUserBio(userId, bioData) {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Handle both old format (string) and new format (object)
      const requestBody = typeof bioData === 'string' 
        ? { bio: bioData }
        : {
            name: bioData.name || '',
            email: bioData.email || '',
            phone: bioData.phone || '',
            website: bioData.website || '',
            location: bioData.location || '',
            bio: bioData.bio || '',
            profile_image: bioData.profile_image || '',
            links: bioData.links || [],
            privacy_settings: bioData.privacy_settings || {}
          };

      console.log('🔄 Updating user bio...', requestBody);

      const response = await fetch(`${API_BASE_URL}users/bio`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Update bio response:', data);
      
      if (data.success) {
        return { success: true, data: data.data || {} };
      } else {
        throw new Error(data.error || 'Failed to update bio');
      }
    } catch (error) {
      console.error('API Error (updateUserBio):', error);
      return { success: false, error: error.message };
    }
  }

  // Devotions CRUD methods
  static async getAllDevotions() {
    try {
      console.log('🔄 Fetching all devotions...');
      // Use legacy endpoint since .htaccess routing is not working
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'all' })
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Devotions data:', data);
      
      if (data.success) {
        return data.data || [];
      } else {
        throw new Error(data.error || 'Failed to fetch devotions');
      }
    } catch (error) {
      console.error('❌ API Error (getAllDevotions):', error);
      throw error;
    }
  }

  static async createDevotion(devotionData) {
    try {
      console.log('🔄 Creating devotion...');
      console.log('📤 Devotion data:', devotionData);
      
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'create',
          ...devotionData
        })
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Create response:', data);
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to create devotion');
      }
    } catch (error) {
      console.error('❌ API Error (createDevotion):', error);
      throw error;
    }
  }

  // Get today's devotion with recent devotions
  static async getTodaysDevotion() {
    try {
      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'today'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          today: data.today,
          recent: data.recent || []
        };
      } else {
        throw new Error(data.error || 'Failed to fetch today\'s devotion');
      }
    } catch (error) {
      console.error('API Error (getTodaysDevotion):', error);
      throw error;
    }
  }

  static async updateDevotion(devotionId, devotionData) {
    try {
      console.log('🔄 Updating devotion:', devotionId);
      console.log('📤 Update data:', devotionData);
      
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'update',
          id: devotionId,
          ...devotionData
        })
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Update response:', data);
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to update devotion');
      }
    } catch (error) {
      console.error('❌ API Error (updateDevotion):', error);
      throw error;
    }
  }

  static async deleteDevotion(devotionId) {
    try {
      console.log('🔄 Deleting devotion:', devotionId);
      
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'delete',
          id: devotionId
        })
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Delete response:', data);
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to delete devotion');
      }
    } catch (error) {
      console.error('❌ API Error (deleteDevotion):', error);
      throw error;
    }
  }

  static async getDevotionById(devotionId) {
    try {
      console.log('🔄 Fetching devotion by ID:', devotionId);
      
      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'by-id',
          id: devotionId
        })
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 Devotion data:', data);
      
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.error || 'Failed to fetch devotion');
      }
    } catch (error) {
      console.error('❌ API Error (getDevotionById):', error);
      throw error;
    }
  }

  // Get previous devotions (before today)
  static async getPreviousDevotions() {
    try {
      // Use direct api.php call since .htaccess routing is broken
      const response = await fetch(`${API_BASE_URL}api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'previous'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data || [];
      } else {
        throw new Error(data.error || 'Failed to fetch previous devotions');
      }
    } catch (error) {
      console.error('API Error (getPreviousDevotions):', error);
      throw error;
    }
  }

  // Get recent devotions (past 5 days)
  static async getRecentDevotions() {
    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'recent'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data || [];
      } else {
        throw new Error(data.error || 'Failed to fetch recent devotions');
      }
    } catch (error) {
      console.error('API Error (getRecentDevotions):', error);
      throw error;
    }
  }

  static async getDevotionByDate(date) {
    try {
      // Get all devotions and filter by date
      const allDevotions = await this.getAllDevotions();
      const devotionForDate = allDevotions.find(devotion => devotion.date === date);
      
      if (devotionForDate) {
        return devotionForDate;
      } else {
        throw new Error('No devotion found for the specified date');
      }
    } catch (error) {
      console.error('API Error (getDevotionByDate):', error);
      throw error;
    }
  }

  // Get author profile (public endpoint)
  static async getAuthorProfile(authorId) {
    try {
      console.log('🔄 Fetching author profile for ID:', authorId);
      
      // Try the new RESTful endpoint first
      const restResponse = await fetch(`${API_BASE_URL}author/${authorId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      let data;
      if (restResponse.ok) {
        data = await restResponse.json();
        console.log('📥 Author data:', data);
        
        if (data.success) {
          return data.author;
        } else {
          throw new Error(data.error || 'Failed to fetch author profile');
        }
      } else {
        // Fallback: try legacy approach if the RESTful endpoint fails
        console.log('⚠️ RESTful endpoint failed, trying fallback...');
        throw new Error('Author profile not found');
      }
    } catch (error) {
      console.error('❌ API Error (getAuthorProfile):', error);
      throw error;
    }
  }

  // Utility methods
  static async isAuthenticated() {
    return !!(await AsyncStorage.getItem('authToken'));
  }

  static async getCurrentUser() {
    const userStr = await AsyncStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  static async clearAuthData() {
    await AsyncStorage.removeItem('authToken');
    await AsyncStorage.removeItem('user');
  }
}

export default ApiService;